import React, { useState } from 'react'

import { Spinner, ToggleSwitch } from '@acorns/web-components'

import {
  OPTIMIZELY_CONTROL_EXPERIMENT,
  OPTIMIZELY_EXPERIMENT_VARIATION_1,
  OPTIMIZELY_EXPERIMENT_VARIATION_2,
} from 'app-constants'
import Nav from 'components/Nav'
import { isMobile } from 'components/Trackers/platform'
import { ProductKey, TierOption } from 'generated/types'
import { WithOptimizelyProps } from 'utils/Optimizely'
import { pricingScreenExperiment } from 'utils/Optimizely/features'

import enhance from './enhance'
import {
  CarouselTierItemContainer,
  Container,
  Headline,
  SpinnerContainer,
  StyledCarousel,
  SubContainer,
  Subheadline,
  SwitchContainer,
  TiersWrapper,
} from './styled-components'
import Tier from './Tier'

type Props = {
  isAuthenticated: boolean
  preferredTierId: string
  tiers: TierOption[]
  headline: string
  subheadline: string
  displayedProductKeys: string[]
  loading: boolean
} & WithOptimizelyProps

const SelectTier = ({
  isAuthenticated,
  preferredTierId,
  tiers,
  displayedProductKeys,
  loading,
  headline,
  subheadline,
  getVariation,
}: Props) => {
  const optimizedVariable = getVariation(pricingScreenExperiment)
  const [toggleChecked, setToggleChecked] = useState<boolean>(false)

  const handleSwitchChange = () => {
    setToggleChecked(!toggleChecked)
  }

  const productKeys = tiers?.map(({ tier }) => tier.products.map(({ key }) => key.toLowerCase()))

  const promotedProductKeys = productKeys
    ? (productKeys[0].length > productKeys[1].length ? productKeys[0] : productKeys[1]).filter(
        key => key !== ProductKey.SelfDirectedInvest.toLowerCase(),
      )
    : []

  const optionLabelsV1 = (tiers || []).map(({ tier }) => tier.name).reverse()

  return (
    <>
      <Nav />
      <Container>
        {(optimizedVariable === OPTIMIZELY_CONTROL_EXPERIMENT ||
          (optimizedVariable === OPTIMIZELY_EXPERIMENT_VARIATION_2 && !isMobile())) && (
          <>
            <Headline>{headline}</Headline>
            <Subheadline>{subheadline}</Subheadline>
          </>
        )}
        {loading ? (
          <SpinnerContainer>
            <Spinner />
          </SpinnerContainer>
        ) : optimizedVariable === OPTIMIZELY_EXPERIMENT_VARIATION_2 && isMobile() ? (
          <StyledCarousel
            customSlickSettings={{
              dots: true,
              infinite: true,
              speed: 500,
              slidesToShow: 1,
              slidesToScroll: 1,
              draggable: false,
              arrows: false,
            }}
          >
            {tiers?.length &&
              tiers.map(tier => (
                <CarouselTierItemContainer key={tier.tier?.id}>
                  <TiersWrapper>
                    <Tier
                      tierData={tier}
                      preferredTierId={preferredTierId}
                      displayedProductKeys={displayedProductKeys}
                      isAuthenticated={isAuthenticated}
                      optimizelyVariant={optimizedVariable}
                      productKeys={promotedProductKeys}
                    />
                  </TiersWrapper>
                </CarouselTierItemContainer>
              ))}
          </StyledCarousel>
        ) : (
          <SubContainer variant={optimizedVariable}>
            {optimizedVariable === OPTIMIZELY_EXPERIMENT_VARIATION_1 && (
              <SwitchContainer>
                <ToggleSwitch
                  checked={toggleChecked}
                  onChange={handleSwitchChange}
                  optionLabels={optionLabelsV1}
                />
              </SwitchContainer>
            )}
            <TiersWrapper>
              {optimizedVariable === OPTIMIZELY_EXPERIMENT_VARIATION_1 ? (
                <Tier
                  tierData={toggleChecked ? tiers[0] : tiers[1]}
                  preferredTierId={preferredTierId}
                  displayedProductKeys={displayedProductKeys}
                  isAuthenticated={isAuthenticated}
                  key={toggleChecked ? tiers[0].tier?.id : tiers[1].tier?.id}
                  optimizelyVariant={optimizedVariable}
                  productKeys={promotedProductKeys}
                />
              ) : (
                tiers?.length &&
                tiers.map(tier => (
                  <Tier
                    tierData={tier}
                    preferredTierId={preferredTierId}
                    displayedProductKeys={displayedProductKeys}
                    isAuthenticated={isAuthenticated}
                    key={tier.tier?.id}
                    optimizelyVariant={optimizedVariable}
                    productKeys={promotedProductKeys}
                  />
                ))
              )}
            </TiersWrapper>
          </SubContainer>
        )}
      </Container>
    </>
  )
}

export default enhance(SelectTier)
